import ImageMeta from './ImageMeta.js'

// TOC

// Introduction
// Fixation: Conservative Measures, Internal Fixation
// Arthrodesis
// Arthroplasty: Shoulder, Hip, Knee, Ankle
// Hardware Failure: Dislocation, Hardware Fracture, Periprosthetic Fractures,
//   Heterotopic ossification, Osteolysis/Loosening
// Conclusion

export default {
  sections: [
    {
      title: 'Fixation',
      text: `
        <p>The goal of fixation is to reduce the distance between facture fragments and to stabilize
          fracture fragments to facilitate healing and proper alignment. Additionally, fracture fixation
        encourage early mobilization in the patient.</p>
      `,
      subsections: [
        {
          title: 'Conservative Measures',
          slides: [
            {
              id: 'cast',
              img: ImageMeta.fixation.cast.examples[0],
              text: `
                <p>
                  Conservative measures of fixation involves initial closed reduction and then stabalization
                  splints, casts, or traction devices.
                  A splint is often used for initial stablization prior to casting or internal fixation. The
                  splint is adjustable and allows for swelling.
                </p>
                <p>
                  On radiographs the splint material is more irregular and is often not circumferential.
                </p>
                `,
              textLayout: {
                pos: 'top-right',
                bg: true,
                w: 40
              },
              caption: {
                pos: 'center-left',
                bg: false,
                w: 30
              }
            },
            {
              id: 'splint',
              img: ImageMeta.fixation.splint.examples[0],
              text: '',
              textLayout: {
                pos: 'bottom-right',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'top-right',
                bg: true,
                w: 50
              }
            },
            {
              id: 'pin1',
              img: ImageMeta.fixation.percutaneous_pin.examples[0],
              text: `
                <p>Traction devices include Kirschner (K) wires and percutaneous pins. These are
                placed along the axis of long bones and provide stablization without open surgery.
                They can be easily removed when needed. An additional advantage of K wires
                is that they are thin and smooth and wont damage the growth plates in children.</p>
              `,
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'center-right',
                bg: false,
                w: 50
              }
            },
            {
              id: 'pin2',
              img: ImageMeta.fixation.percutaneous_pin.examples[1],
              text: '',
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'center-left',
                bg: false,
                w: 20
              }
            },
            {
              id: 'exfix',
              img: ImageMeta.fixation.exfix.examples[0],
              text: `
                <p>External fixation is used with extensive soft tissue injury or polytrauma. It is also used
                temporarily before internal fixation and during arthrodesis. Frequent skin care for the external
                pins to prevent infection and radiography to look for hardware failure are important.</p>
                `,
              textLayout: {
                pos: 'top-right',
                bg: true,
                w: 40
              },
              caption: {
                pos: 'center-right',
                bg: true,
                w: 25
              }
            }
          ]
        },
        {
          title: 'Internal Fixation',
          slides: [
            {
              id: 'platescrew',
              img: ImageMeta.fixation.platescrew.examples[0],
              imgAlign: 'right',
              text: `
                    <p>Open reduction and internal fixation is performed when a
                closed option is not possible. This enables early mobilization and function
                which promotes fast rehabiliation. There is a wide variety of internal
                fixation hardware, which includes many types of pins, wires, screws, plates,
                and rods with different indications.</p>
                `,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 50
              }
            },
            {
              id: 'screw',
              img: ImageMeta.fixation.screws.examples[0],
              imgAlign: 'left',
              text: `
                `,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'top-right',
                bg: false,
                w: 30
              }
            },
            {
              id: 'screw2',
              img: ImageMeta.fixation.screws.examples[1],
              imgAlign: 'right',
              text: `
                `,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 50
              }
            },
            {
              id: 'spiral',
              img: ImageMeta.fixation.spiral.examples[0],
              imgAlign: 'right',
              text: `
                    <p >Compression can be static or
                dynamic. Static compression is the compression provided by the hardware.
                Dynamic compression utilizes the body's own motions to provide additional
                compression. </p>

                  <p>The spiral blade is typically used for an
                    intertrochanteric fracture and allows for dynamic compression. Similarly
                    used is the dynamic compression screw, which consists of a lag screw
                    which fits in the barrel of a side plate. The side plate is fixed to the
                    femur and the barrel allows the lag screw to move within it. Ambulation
                    provides dynamic compression.</p>
                `,
              textLayout: {
                pos: 'bottom-left',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'top-left',
                bg: true,
                w: 40
              }
            },
            {
              id: 'dynamic',
              img: ImageMeta.fixation.dynamic.examples[0],
              imgAlign: 'center',
              text: `
                `,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'top-left',
                bg: true,
                w: 80
              }
            },
            {
              id: 'imnail',
              img: ImageMeta.fixation.imnail.examples[0],
              imgAlign: 'center',
              text: `
                <p>Intramedullary nails are placed percutaneously.
                These are thick rods that are used to stablize shaft fractures which are
                not near joints against bending. Optional interlocking screws are often
                places at the proximal and distal ends of the nail to prevent rotation
                and shortening. Once some healing has occured the distal screws can be
                removed to allow faster healing due to dynamic compression. </p>
                `,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'imnail2',
              img: ImageMeta.fixation.imnail.examples[1],
              imgAlign: 'center',
              text: `
                `,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 50
              }
            },
            {
              id: 'hagie',
              img: ImageMeta.fixation.hagie.examples[0],
              imgAlign: 'center',
              text: `
                  <p>The Hagie pin is a type of partially threaded
                intramedullary pin which has a threaded end to secure the distal fragment
                and a nut optionally applied to the other end for compression, typically
                used for clavicular fractures.</p>
                `,
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 60
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'cerclage',
              img: ImageMeta.fixation.cerclage.examples[0],
              imgAlign: 'left',
              text: `
                <p>Cerclage wires or cables (made of multiple wires) are
                used in combination with other fixation hardware and they go around the bone
                to hold fragments together.</p>
                `,
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'center-right',
                bg: false,
                w: 40
              }
            },
            {
              id: 'tension',
              img: ImageMeta.fixation.tension.examples[0],
              imgAlign: 'left',
              text: `
                <p>The tension wire and K-wires are often used
                for displaced fragments, typically in the patella and olecrenon. They
                provide dynamic compression.</p>
                `,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 80
              }
            },
            {
              id: 'tension2',
              img: ImageMeta.fixation.tension.examples[1],
              imgAlign: 'center',
              text: `
                `,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 50
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 50
              }
            },
            {
              id: 'bladeplate',
              img: ImageMeta.fixation.bladeplate.examples[0],
              imgAlign: 'center',
              text: `
                <p>Blade plates have a fixed angle top and are
                often used for subtrochanteric osteotomies or fractures. The blade
                portion goes into the metaphysis.</p>
                `,
              textLayout: {
                pos: 'top-right',
                bg: true,
                w: 40
              },
              caption: {
                pos: 'top-left',
                bg: true,
                w: 40
              }
            },
            {
              id: 'syndesmotic-screw',
              img: ImageMeta.fixation.syndesmotic.examples[0],
              imgAlign: 'right',
              text: `
                <!-- anchor screws --> <p>Anchor screws are used for tendon and ligament
                repair. One end is inserted through the cortex of the bone and the other
                end has an attachment site for suture material, which ties into the
                ligament or tendon.</p>

                <!-- syndesmotic screw --> <p>The syndesmotic screw spans the
                tibiofibular syndesmosis. It may be placed in isolation or through a
                plate. Be on the lookout for loosening as this is one of the most common
                sites for this to occur, as there is a lot of motion. These are
                typically removed after 6-12 weeks.</p>
                `,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'top-right',
                bg: false,
                w: 40
              }
            }
          ]
        }
      ]
    },
    {
      title: 'Arthrodesis',
      subsections: [
        {
          slides: [
            {
              id: 'arthrodesis',
              img: ImageMeta.fixation.arthrodesis.examples[0],
              imgAlign: 'right',
              text: `
                <p>Arthrodesis is the fusion of at least two bones. This is done to stop the progression of
                  arthritic disease and alleviate pain. It is also used to correct deformities in the extremities,
                  such as pes planus.</p>

                  <!-- four corner -->
                <p>The four corner arthrodesis is used in the case of scaphoid nonunion when the lunate is still
                  healthy. It involves scaphoidectomy and fusion of the hamate, lunate, capitate and triquetrum.
                  If there is lunate osteonecrosis, a proximal row carpectomy is preferred.</P>
                `,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'top-right',
                bg: true,
                w: 40
              }
            },
            {
              id: 'syndesmotic',
              img: ImageMeta.fixation.arthrodesis.examples[1],
              imgAlign: 'right',
              text: `
                <p>The triple arthrodesis is used for Chopard's joint <!-- spelling -->  (often involved in neuropathic joint) and
                  to fix hindfoot or forefoot deformities such as pes planus. It involves fusion of the
                  subtalar, calcaneocuboid, and talonavicular joints. </p>
                `,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 30
              }
            }
          ]
        }
      ]
    },
    {
      title: 'Arthroplasty',
      subsections: [
        {
          title: 'Shoulder',
          slides: [
            {
              id: 'resurfacing',
              img: ImageMeta.shoulder.resurfacing.examples[0],
              imgAlign: 'right',
              text: `
                <p>A hemiarthroplasty is used for fractures or avascular necrosis of the humeral head
                   or rotator cuff tears. You can tell that the glenoid is intact because there is no metal there.</p>

                <p>A resurfacing hemiarthroplasty is similar except only the humeral head is replaced. There is no stem.</p>
                `,
              textLayout: {
                pos: 'bottom-right',
                bg: true,
                w: 50
              },
              caption: {
                pos: 'center-left',
                bg: true,
                w: 30
              }
            },
            {
              id: 'totalshoulder1',
              img: ImageMeta.shoulder.total.examples[0],
              imgAlign: 'left',
              text: `
                <p>A conventional arthroplasty is used when there is no rotator cuff deficiency, such as arthritis.</p>
                `,
              textLayout: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              },
              caption: {
                pos: 'top-right',
                bg: true,
                w: 40
              }
            },
            {
              id: 'totalshoulder2',
              img: ImageMeta.shoulder.total.examples[1],
              imgAlign: 'center',
              text: `
                `,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'reverse',
              img: ImageMeta.shoulder.reverse.examples[0],
              imgAlign: 'left',
              text: `
                  <p>A reverse total arthroplasty is used for rotator cuff tears or after failed conventional arthroplasty.
                  This uses the deltoid muscle for action and is a good choice if there is rotator cuff deficiency.</P>

                  <p>The glenoid componennt may be screwed or cemented and the humeral component may or may not be cemented.</p>
                `,
              textLayout: {
                pos: 'center-right',
                bg: true,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            }
          ]
        },
        {
          title: 'Hip',
          slides: [
            {
              id: 'hip',
              img: ImageMeta.hip.unipolar.examples[0],
              imgAlign: 'right',
              text: `
              <p>Indications for hip arthroplasty include: arthritis, infection, trauma, and avascular necrosis.</p>

              <p>A unipolar hemiarthroplasty is used in patients with low life expectancy and only contains a femoral
                component.</p>
                `,
              textLayout: {
                pos: 'center-right',
                bg: true,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'hip1',
              img: ImageMeta.hip.unipolar.examples[1],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: true,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'hip2',
              img: ImageMeta.hip.bipolar.examples[0],
              imgAlign: 'left',
              text: `
              <p>Bipolar hemiarthroplasty is used for patients with Garden 4 subcapital fractures
                that have a preserved joint in order to manage the risk of avascular necrosis. It is less
                prone to dislocation and damage to the native acetabular fossa. There is a femoral stem
                and a femoral shell that snap together. There is no acetabular component. The femoral stem
              can rotate within the shell and the shell within the acetabular. Since there are two mobile
              components, there is less motion and less wear on the native acetabulum.
              </p>`,
              textLayout: {
                pos: 'top-left',
                bg: true,
                w: 30
              },
              caption: {
                pos: 'bottom-right',
                bg: true,
                w: 95
              }
            },
            {
              id: 'hip3',
              img: ImageMeta.hip.bipolar.examples[1],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'hip4',
              img: ImageMeta.hip.total.examples[0],
              imgAlign: 'right',
              text: `
              <p>Total arthroplasty is used for arthritis. There is a separate femoral and acetabular
                component. Usually it is metal against polyethylene.</p>

              <p>Cement may be used. If no cement is used a bioactive coating (hydroxyapetite) <!-- check --> or rough surface of the
                hardware stimulates osseous growth. Screws may be used in the acetabular component. It
                is most common to see a cemented femoral component and a non-cemented acetabular component.</p>
            `,
              textLayout: {
                pos: 'top-right',
                bg: true,
                w: 30
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'hip5',
              img: ImageMeta.hip.total.examples[1],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'center-left',
                bg: false,
                w: 30
              }
            },
            {
              id: 'hip6',
              img: ImageMeta.hip.spacer.examples[0],
              imgAlign: 'center',
              text: `<p>An articulating antibiotic spacer is often used with infected joints and may be mistaken for a
                total hip arthroplasty. Look for thick cemented.</p>`,
              textLayout: {
                pos: 'top-left',
                bg: true,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 50
              }
            }
          ]
        },
        {
          title: 'Knee',
          slides: [
            {
              id: 'unicompartmental',
              img: ImageMeta.knee.unicompartmental.examples[0],
              imgAlign: 'left',
              text: `<p>Knee replacement is often for arthritis. Again, there are several different types.</p>

              <p>A unicompartmental replacement is often done on the medial joint. There is a femoral
                condyle component and a corresponding tibial component.</p>`,
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'unicompartmental1',
              img: ImageMeta.knee.unicompartmental.examples[1],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 30
              }
            },
            {
              id: 'nostemtotal',
              img: ImageMeta.knee.nostemtotal.examples[0],
              imgAlign: 'right',
              text: `<p>A total knee arthroplasty involves resurfacing of all three joint compartments. The
                components may have no stem, a short stem, or a long stem. Cement may also be used.
              With increasing bone loss nad more revisions, a longer stem component is necessary. Additonally,
              various levels of contraint may be employed to increase stability of the joint.</p>`,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'nostemtotal1',
              img: ImageMeta.knee.nostemtotal.examples[1],
              imgAlign: 'left',
              text: `<p>Note that the articular aspect of the patella has also been resurfaced and
                now has a radiolucent polyethylene lining.</p>`,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'knee',
              img: ImageMeta.knee.nostemtotal.examples[2],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'top-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'pclsparing',
              img: ImageMeta.knee.pclsparing.examples[0],
              imgAlign: 'right',
              text: `<p>Total knee arthroplasties can have varying levels of restricted mobility.
                The least restricted is the PCL-sparing or nonconstrained arthroplasty, which allow for
                some rotation and translation of the knee. On the other end of the spectrum, a
                constrained total knee arthroplasty does not allow for any motion except the minimum
                necessary hinged flexion and extension.</p>
                <p>
                You can distinguish these implants by the amount of femoral bone which is replace by the
                metallic hardware and the presence of an metal bar connecting the tibial and femoral components.
                In this case, most of the native femur is preserved.
              </p>`,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'semiconstrained',
              img: ImageMeta.knee.semiconstrained.examples[1],
              imgAlign: 'left',
              text: `<p>At our institution we identify the PCL sacrificing prostheses without
                radioopaque hinges bridging the tibial and femoral components as semiconstrained.
                These can be distinguished by the thick
                femoral component on lateral view, which accomodates
                a central polyethelyene (radiolucent) tibial post that
                resists some rotation and varus-valgus motion.
              </p>`,
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 25
              },
              caption: {
                pos: 'bottom-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'constrained',
              img: ImageMeta.knee.constrained.examples[0],
              imgAlign: 'center',
              text: `<p>The constrained total knee arthroplasty is easily identified by a
                metallic bar connecting the femoral and tibial components.
              </p>`,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 25
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 25
              }
            }
          ]
        },
        {
          title: 'Ankle',
          slides: [
            {
              id: 'ankle',
              img: ImageMeta.ankle.arthroplasty.examples[0],
              imgAlign: 'center',
              text: `<p>Ankle arthroplasty is an alternative to arthrodesis when the subtalar joint is preserved.
                This allows for more ankle mobility.</p>`,
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'ankle2',
              img: ImageMeta.ankle.arthroplasty.examples[1],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 95
              }
            }
          ]
        }
      ]
    },
    {
      title: 'Hardware Failure',
      subsections: [
        {
          title: 'Dislocation',
          slides: [
            {
              id: 'dislocation',
              img: ImageMeta.failure.dislocation.examples[0],
              imgAlign: 'center',
              text: `<p>Two component arthroplasty hardware can dislocate just like the native joints.</p>`,
              textLayout: {
                pos: 'top-right',
                bg: true,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'dislocation-right-hip',
              img: ImageMeta.failure.dislocation.examples[1],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-right',
                bg: true,
                w: 95
              }
            },
            {
              id: 'dislocation-crosstable',
              img: ImageMeta.failure.dislocation.examples[2],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'dislocation-right-hip2',
              img: ImageMeta.failure.dislocation.examples[3],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            }
          ]
        },
        {
          title: 'Periprosthetic Fracture',
          slides: [
            {
              id: 'periprostheticfx',
              img: ImageMeta.failure.periprostheticfx.examples[0],
              imgAlign: 'right',
              text: `<p>The bones supporting the hardware may fracture.</p>`,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'periprostheticfx2',
              img: ImageMeta.failure.periprostheticfx.examples[1],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'top-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'periprostheticfx3',
              img: ImageMeta.failure.periprostheticfx.examples[2],
              imgAlign: 'left',
              text: ``,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'center-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'periprostheticfx4',
              img: ImageMeta.failure.periprostheticfx.examples[3],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-right',
                bg: true,
                w: 95
              }
            },
            {
              id: 'periprostheticfx5',
              img: ImageMeta.failure.periprostheticfx.examples[4],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 50
              }
            }
          ]
        },
        {
          title: 'Hardware Fracture',
          slides: [
            {
              id: 'hardwarefx',
              img: ImageMeta.failure.hardwarefx.examples[0],
              imgAlign: 'left',
              text: `<p>Even though it is made of sturdy material,
                hardware can bend or break under stress.</p>`,
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 60
              },
              caption: {
                pos: 'bottom-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'hardwarefx2',
              img: ImageMeta.failure.hardwarefx.examples[1],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'hardwarefx-screws',
              img: ImageMeta.failure.hardwarefx.examples[2],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'center-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'hardwarefx4',
              img: ImageMeta.failure.hardwarefx.examples[3],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'hardwarefx5',
              img: ImageMeta.failure.hardwarefx.examples[4],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'hardwarefx6',
              img: ImageMeta.failure.hardwarefx.examples[5],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'top-right',
                bg: true,
                w: 95
              }
            }
          ]
        },
        {
          title: 'Osteolysis/Loosening',
          slides: [
            {
              id: 'loosening',
              img: ImageMeta.failure.loosening.examples[0],
              imgAlign: 'center',
              text: `
                <p>Loosening is the most common cause for replacement. This can be due to mechanical
                  stress (wear and tear) or biologic loosening (eg, breakdown of cement).</p>

                <p>Hardware failure often occurs with persistent non-union because of the constant
                  motion which stresses the hardware.</p>`,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'bottom-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening2',
              img: ImageMeta.failure.loosening.examples[1],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 40
              }
            },
            {
              id: 'loosening3',
              img: ImageMeta.failure.loosening.examples[3],
              imgAlign: 'right',
              text: `<p>Loosening of a total hip arthroplasty often involves the acetabular component
                moving inferomedially which causes lateral uncovering of the femoral component.</p>`,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'loosening4',
              img: ImageMeta.failure.loosening.examples[9],
              imgAlign: 'right',
              text: `<p>Subsidence is a type of loosening wherein the prosthesis sinks further within
                the bone, typically the femoral component of a total hip replacement.</p>`,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening5',
              img: ImageMeta.failure.loosening.examples[2],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'center-left',
                bg: false,
                w: 50
              }
            },
            {
              id: 'loosening6',
              img: ImageMeta.failure.loosening.examples[4],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening7',
              img: ImageMeta.failure.loosening.examples[5],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'loosening8',
              img: ImageMeta.failure.loosening.examples[6],
              imgAlign: 'left',
              text: ``,
              textLayout: {
                pos: 'bottom-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'center-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening9',
              img: ImageMeta.failure.loosening.examples[7],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening10',
              img: ImageMeta.failure.loosening.examples[8],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'top-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening11',
              img: ImageMeta.failure.loosening.examples[10],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-left',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'center-right',
                bg: true,
                w: 95
              }
            },
            {
              id: 'loosening12',
              img: ImageMeta.failure.loosening.examples[11],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening13',
              img: ImageMeta.failure.loosening.examples[12],
              imgAlign: 'right',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'center-left',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening14',
              img: ImageMeta.failure.particle.examples[0],
              imgAlign: 'right',
              text: `<p>The body may respond to the foreign body (cement or hardware) with inflammation.
                This eventually results in bony breakdown with large cystic changes/osteolysis.
                This is called particle disease.</p>`,
              textLayout: {
                pos: 'top-left',
                bg: false,
                w: 25
              },
              caption: {
                pos: 'bottom-right',
                bg: false,
                w: 95
              }
            },
            {
              id: 'loosening15',
              img: ImageMeta.failure.particle.examples[1],
              imgAlign: 'center',
              text: `<p>Metallosis is a subset of particle disease wherein a pseudotumor is seen due to
                metal on metal prosthesis (which are now discontinued from the market).</p>`,
              textLayout: {
                pos: 'top-right',
                bg: false,
                w: 30
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            }

          ]
        },
        {
          title: 'Heterotopic ossification',
          slides: [
            {
              id: 'heterotopic',
              img: ImageMeta.failure.heterotopic.examples[0],
              imgAlign: 'left',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: true,
                w: 95
              }
            },
            {
              id: 'heterotopic2',
              img: ImageMeta.failure.heterotopic.examples[1],
              imgAlign: 'center',
              text: ``,
              textLayout: {
                pos: 'center-right',
                bg: false,
                w: 40
              },
              caption: {
                pos: 'bottom-left',
                bg: false,
                w: 95
              }
            }
          ]
        }
      ]
    }
  ]
}



// WEBPACK FOOTER //
// ./src/LessonData.js